<script setup>
import { useNotifier } from '@daysmart/daysmart-vue-components/composables'

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  isAppointment: Boolean,
  isRoomRental: Boolean,
})

const emit = defineEmits(['close', 'update', 'cancel'])

const { sessionData } = useSessionData()

const { hasFamily } = useSessionData()
const notify = useNotifier()

const selectedCustomer = useSelectedCustomer()
const api = useApi()
const { t } = useI18n()
const membershipPenalties = ref()

const isPastLateCancel = computed(() => {
  const registrationTimeline = props.event?.active_registration_status

  if (!registrationTimeline)
    return false

  const lateCancel = new Date(registrationTimeline.late_cancels_after)
  const now = new Date()

  return lateCancel <= now
})

const disableCancelButton = ref(false)
const customerName = ref('')

async function fetchSelectedCustomerName() {
  const result = await api.retrieveCustomer(selectedCustomer.value)

  customerName.value = `${result.first_name} ${result.last_name}`
}

async function fetchCustomerMembership() {
  const result = await api.listAttendances({
    queryParameters: {
      customer: selectedCustomer.value,
      event: props.event.id,
      fields: ['customer_membership'],
    },
  })
  if (result) {
    const customerMembershipId = result.results?.[0].customer_membership
    await fetchCustomerMembershipPenalties(customerMembershipId)
  }
}

async function fetchCustomerMembershipPenalties(customerMembershipId) {
  const result = await api.retrieveCustomerMembership(customerMembershipId, {
    expand: ['membership.penalty_system'],
  })
  if (result)
    membershipPenalties.value = result.membership.penalty_system
}

async function unregister() {
  disableCancelButton.value = true
  const eventId = typeof (props.event.id) === 'number' ? props.event.id : props.event.registeredEventId
  await api.unregisterEvent(eventId, {
    data: { customer: selectedCustomer.value },
  })

  notify.emit({
    type: 'success',
    title: t('Success'),
    content: t('Unregistered from event'),
  })

  if (props.isAppointment || props.isRoomRental) {
    emit('close')
  }
  else {
    emit('update')
  }
}

onBeforeMount(() => {
  if (hasFamily) {
    fetchSelectedCustomerName()
    fetchCustomerMembership()
  }
})

const eventType = computed(() => {
  if (props.isAppointment)
    return t('appointment')
  if (props.isRoomRental)
    return t('room rental')
  return t('class')
})

const lateCancelText = sessionData?.registration_settings?.late_cancellation_note
</script>

<template>
  <div class="flex grow flex-col justify-between flex-gap-y-4">
    <div class="flex flex-col flex-gap-y-2">
      <p class="text-text fw-600">
        {{ isAppointment ? t('Leave Appointment') : isRoomRental ? t('Leave Room Rental') : t('Leave Class') }}
      </p>
      <p
        v-if="!isPastLateCancel"
        class="text-text"
      >
        {{ t('Are you sure you want to leave this {type}?', { type: eventType }) }}
      </p>
      <p
        v-else
        class="text-text"
      >
        {{ lateCancelText }}
      </p>
      <p
        v-if="!!membershipPenalties && membershipPenalties.late_cancel_rule?.max_infractions"
        class="text-text"
      >
        {{ t('late_cancel_rule', { late_cancel: t('late_cancel', membershipPenalties.late_cancel_rule?.max_infractions), days: t('days', membershipPenalties.late_cancel_rule?.days), price: membershipPenalties.late_cancel_rule?.price?.string }) }}
      </p>
      <DSLoadingWrapper
        v-if="hasFamily"
        :loading="!customerName"
      >
        <p class="fw-600">
          {{ t('This will remove {name} from this {type}.', { name: customerName, type: eventType }) }}
        </p>
      </DSLoadingWrapper>
    </div>
    <div class="flex flex-col flex-gap-y-2">
      <DSAsyncButton
        class="btn btn-md w-full uppercase btn-fill-tertiary"
        @submit="unregister()"
      >
        {{ t('Yes') }}
      </DSAsyncButton>
      <DSButton
        class="btn btn-md w-full uppercase btn-fill-secondary"
        :disabled="disableCancelButton"
        @click="emit('cancel')"
      >
        {{ t('No') }}
      </DSButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
[data-ds-component='button'] {
  @apply uppercase;
}
</style>

<i18n>
{
  "en-us": {
    "Leave Class": "Leave Class",
    "Are you sure you want to leave this class?": "Are you sure you want to leave this class?",
    "This will remove {name} from this class.": "This will remove {name} from this class.",
    "Yes": "Yes",
    "No": "No",
    "Unregistered from event": "Unregistered from event",
    "Success": "Success",
    "Leave Appointment": "Leave Appointment",
    "Are you sure you want to leave this appointment?": "Are you sure you want to leave this appointment?",
    "This will remove {name} from this appointment.": "This will remove {name} from this appointment.",
    "Are you sure you want to leave this class ?": "Are you sure you want to leave this class ?",
    "It is currently after the cancellation deadline! You may still unregister to open up a spot for someone else but this will still be counted against your membership usage limits and you will not receive a refund.": "It is currently after the cancellation deadline! You may still unregister to open up a spot for someone else but this will still be counted against your membership usage limits and you will not receive a refund.",
    "late_cancel": "1 time | {n} times",
    "days": "1 day | {n} days",
    "late_cancel_rule": "If you Late Cancel {late_cancel} within {days} you will be subject to a fee of {price}",
    "Leave Room Rental": "Leave Room Rental",
    "Are you sure you want to leave this {type}?": "Are you sure you want to leave this {type}?",
    "This will remove {name} from this {type}.": "This will remove {name} from this {type}.",
    "appointment": "appointment",
    "room rental": "room rental",
    "class": "class"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Leave Class": "Kurs verlassen",
  "Are you sure you want to leave this class?": "Diesen Kurs wirklich verlassen?",
  "It is currently after the cancellation deadline! You may still unregister to open up a spot for someone else but this will still be counted against your membership usage limits and you will not receive a refund.": "Die Stornierungsfrist ist abgelaufen. Sie können sich zwar abmelden, damit ein Platz für jemand anders frei wird, aber dies verstößt immer noch gegen die Mitgliedschafts-Nutzungsbeschränkungen, somit erhalten Sie keine Rückerstattung.",
  "This will remove {name} from this class.": "Dadurch wird {name} aus diesem Kurs entfernt.",
  "Yes": "Ja",
  "No": "Nein",
  "Unregistered from event": "Von Veranstaltung abgemeldet",
  "Success": "Erfolg",
  "Leave Appointment": "Termin verlassen",
  "Are you sure you want to leave this appointment?": "Wirklich diesen Termin verlassen?",
  "This will remove {name} from this appointment.": "Dadurch wird {name} von diesem Termin entfernt.",
  "Are you sure you want to leave this class ?": "Diesen Kurs wirklich verlassen?"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Leave Class": "Quitter le cours",
  "Are you sure you want to leave this class?": "Souhaitez-vous vraiment quitter ce cours?",
  "It is currently after the cancellation deadline! You may still unregister to open up a spot for someone else but this will still be counted against your membership usage limits and you will not receive a refund.": "Le délai d'annulation prévu est dépassé. Vous pouvez encore vous désinscrire pour libérer une place pour quelqu'un d'autre, mais cette séance sera débitée de votre abonnement et vous ne serez pas remboursé(e)",
  "This will remove {name} from this class.": "Cela retirera {name} de ce cours.",
  "Yes": "Oui",
  "No": "Non",
  "Unregistered from event": "Désinscrit de l'événement",
  "Success": "Succès",
  "Leave Appointment": "Quitter le rendez-vous",
  "Are you sure you want to leave this appointment?": "Voulez-vous vraiment quitter ce rendez-vous ?",
  "This will remove {name} from this appointment.": "Cela retirera {name} de ce rendez-vous.",
  "Are you sure you want to leave this class ?": "Souhaitez-vous vraiment quitter ce cours ?"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Leave Class": "Salir de la clase",
  "Are you sure you want to leave this class?": "¿Seguro que quiere abandonar esta clase?",
  "It is currently after the cancellation deadline! You may still unregister to open up a spot for someone else but this will still be counted against your membership usage limits and you will not receive a refund.": "¡La fecha límite de cancelación ya se ha superado! Aún puede cancelar la inscripción y liberar su plaza, pero su inscripción seguirá teniéndose en cuenta de cara a los límites de uso de su suscripción y su cancelación no implicará ningún reembolso.",
  "This will remove {name} from this class.": "Esto eliminará a {name} de esta clase.",
  "Yes": "Sí",
  "Unregistered from event": "Se ha cancelado la inscripción para este evento",
  "Success": "Correcto",
  "Leave Appointment": "Abandonar cita",
  "Are you sure you want to leave this appointment?": "¿Seguro que quiere abandonar esta cita?",
  "This will remove {name} from this appointment.": "Esto eliminará {name} de esta cita.",
  "Are you sure you want to leave this class ?": "¿Seguro que quiere salir de esta clase?"
}
</i18n>
